<script lang="ts">
	import DOMPurify from 'dompurify';
	import { remarkCitations } from '$lib/markdown/remark-citations';
	import { remarkSourceHighlights } from '$lib/markdown/remark-source-highlights';
	import type {
		CitationAttrs,
		CitationMdastNode,
		SourceHighlightMdastNode
	} from '$lib/markdown/types';
	import SourceHighlight from '$lib/SourceHighlight.svelte';
	import SourceRefBadge from '$lib/SourceRefBadge.svelte';
	import type {
		Blockquote,
		Code,
		Emphasis,
		Heading,
		Html,
		Image,
		InlineCode,
		Link,
		List,
		ListItem,
		Paragraph,
		PhrasingContent,
		Root,
		RootContent,
		RowContent,
		Strong,
		Table,
		TableCell,
		Text
	} from 'mdast';
	import remarkGfm from 'remark-gfm';
	import remarkParse from 'remark-parse';
	import type { Snippet } from 'svelte';
	import { unified } from 'unified';

	interface Props {
		source: string;
		onRefClick?: (attrs: CitationAttrs) => void;
		children?: Snippet;
	}

	let { source, onRefClick, children }: Props = $props();

	const processor = unified()
		.use(remarkParse)
		.use(remarkGfm)
		.use(remarkCitations)
		.use(remarkSourceHighlights);

	function isCitationTag(value: string): boolean {
		return /^<\/?citation[\s>]/i.test(value.trim());
	}

	const ast = $derived.by(() => {
		const tree = processor.parse(source) as Root;
		processor.runSync(tree);
		return tree;
	});
</script>

{#snippet renderChildren(nodes: (RootContent | PhrasingContent | RowContent | TableCell)[])}
	{#each nodes as child, i (i)}{@render renderMarkdown(child)}{/each}
{/snippet}

{#snippet renderRoot(n: Root)}
	{@render renderChildren(n.children)}
{/snippet}

{#snippet renderParagraph(n: Paragraph)}
	<p>{@render renderChildren(n.children)}</p>
{/snippet}

{#snippet renderHeading(n: Heading)}
	{#if n.depth === 1}<h1>{@render renderChildren(n.children)}</h1>
	{:else if n.depth === 2}<h2>{@render renderChildren(n.children)}</h2>
	{:else if n.depth === 3}<h3>{@render renderChildren(n.children)}</h3>
	{:else if n.depth === 4}<h4>{@render renderChildren(n.children)}</h4>
	{:else if n.depth === 5}<h5>{@render renderChildren(n.children)}</h5>
	{:else}<h6>{@render renderChildren(n.children)}</h6>
	{/if}
{/snippet}

{#snippet renderText(n: Text)}{n.value}{/snippet}

{#snippet renderStrong(n: Strong)}
	<strong>{@render renderChildren(n.children)}</strong>
{/snippet}

{#snippet renderEmphasis(n: Emphasis)}
	<em>{@render renderChildren(n.children)}</em>
{/snippet}

{#snippet renderInlineCode(n: InlineCode)}
	<code>{n.value}</code>
{/snippet}

{#snippet renderCode(n: Code)}
	<pre><code class={n.lang ? `language-${n.lang}` : undefined}>{n.value}</code></pre>
{/snippet}

{#snippet renderLink(n: Link)}
	<a href={n.url} target="_blank" rel="noopener noreferrer">{@render renderChildren(n.children)}</a>
{/snippet}

{#snippet renderImage(n: Image)}
	<img src={n.url} alt={n.alt ?? ''} />
{/snippet}

{#snippet renderBlockquote(n: Blockquote)}
	<blockquote>{@render renderChildren(n.children)}</blockquote>
{/snippet}

{#snippet renderList(n: List)}
	{#if n.ordered}
		<ol start={n.start ?? 1}>{@render renderChildren(n.children)}</ol>
	{:else}
		<ul>{@render renderChildren(n.children)}</ul>
	{/if}
{/snippet}

{#snippet renderListItem(n: ListItem)}
	<li>{@render renderChildren(n.children)}</li>
{/snippet}

{#snippet renderTable(n: Table)}
	<div class="table-wrapper">
		<table>
			<thead
				><tr
					>{#each n.children[0]?.children ?? [] as cell, i (i)}{@render renderMarkdown(
							cell
						)}{/each}</tr
				></thead
			>
			<tbody>
				{#each n.children.slice(1) as row, i (i)}
					<tr
						>{#each row.children as cell, j (j)}{@render renderMarkdown(cell)}{/each}</tr
					>
				{/each}
			</tbody>
		</table>
	</div>
{/snippet}

{#snippet renderTableCell(n: TableCell)}
	<td>{@render renderChildren(n.children)}</td>
{/snippet}

{#snippet renderThematicBreak()}<hr />{/snippet}

{#snippet renderBreak()}<br />{/snippet}

{#snippet renderHtml(n: Html)}
	{#if !isCitationTag(n.value)}
		<!-- eslint-disable-next-line svelte/no-at-html-tags -->
		{@html DOMPurify.sanitize(n.value)}
	{/if}
{/snippet}

{#snippet renderCitation(n: CitationMdastNode)}
	<SourceRefBadge {...n.attrs} {onRefClick} />
{/snippet}

{#snippet renderSourceHighlight(n: SourceHighlightMdastNode)}
	<SourceHighlight hashes={n.hashes} text={n.text} />
{/snippet}

{#snippet renderMarkdown(n: Root | RootContent | PhrasingContent | RowContent | TableCell)}
	{@const dispatch: Record<string, Snippet<[any]>> = {
		root: renderRoot,
		paragraph: renderParagraph,
		heading: renderHeading,
		text: renderText,
		strong: renderStrong,
		emphasis: renderEmphasis,
		inlineCode: renderInlineCode,
		code: renderCode,
		link: renderLink,
		image: renderImage,
		blockquote: renderBlockquote,
		list: renderList,
		listItem: renderListItem,
		table: renderTable,
		tableCell: renderTableCell,
		thematicBreak: renderThematicBreak,
		break: renderBreak,
		html: renderHtml,
		citation: renderCitation,
		sourceHighlight: renderSourceHighlight
	}}
	{@render dispatch[n.type]?.(n)}
{/snippet}

<div class="markdown" role="presentation">
	{@render renderMarkdown(ast)}
	{@render children?.()}
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.markdown {
		overflow: hidden;
		& > * {
			&:first-child {
				margin-top: 0;
			}
			&:last-child {
				margin-bottom: 0;
			}
		}

		h1,
		h2,
		h3,
		h4,
		h5,
		h6 {
			color: var(--text-primary);
		}

		h1 {
			font-family: 'Roboto', sans-serif;
			font-size: 2em;
			margin: 0.67em 0;
		}
		h2 {
			font-size: 1.5em;
			margin: 0.75em 0;
		}
		h3 {
			font-size: 1.17em;
			margin: 0.83em 0;
		}
		h4,
		p,
		fieldset,
		form,
		ul,
		ol,
		dl,
		dir,
		menu {
			margin: 1.12em 0;
		}
		blockquote {
			padding: 0.5rem 1rem;
		}
		h5 {
			font-size: 0.83em;
			margin: 1.5em 0;
		}
		h6 {
			font-size: 0.75em;
			margin: 1.67em 0;
		}
		img {
			max-width: 100%;
			border-radius: 0.5rem;
		}
		a {
			text-decoration: underline;
		}
		.table-wrapper {
			overflow-x: auto;
			max-width: 100%;
			background: var(--surface-3);
			border: 1px solid var(--border-subtle);
			border-radius: 0.5rem;
			margin: 1rem 0;
			justify-self: flex-start;
			table {
				border-spacing: 0;
				border-collapse: collapse;
				width: max-content;
				th,
				td {
					max-width: 14rem;
				}

				th,
				thead td {
					text-align: left;
					border-bottom: 2px solid var(--border-subtle);
					padding: 0.5rem;
				}

				td {
					padding: 0.25rem 0.5rem;
				}

				tr:not(:last-child) td {
					border-bottom: 1px solid var(--border-subtle);
				}
			}
		}

		ul,
		ol {
			li p {
				margin: 0;
			}
		}
	}
</style>
